import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline, Icon } from "@/components/primitives";

interface Item { q: string; a: string }

const FAQS: Item[] = [
  {
    q: "How do I scan an item?",
    a: "Tap the scan button on the home screen, photograph your garment's label and any key details (stitching, hardware, fabric), then submit. FIG returns a verdict in seconds.",
  },
  {
    q: "What does PASS / FAIL mean?",
    a: "PASS means our model found the markers consistent with the stated era and brand. FAIL means we found red flags — synthetic materials, incorrect label fonts, or construction that doesn't match the claimed origin.",
  },
  {
    q: "Why does my scan show low confidence?",
    a: "Low confidence usually means the photos were blurry, cropped too tightly, or missing the label. Retake with good lighting, include the full tag, and add a fabric close-up for the best result.",
  },
  {
    q: "How do I post an item to the feed?",
    a: "After your scan comes back PASS, tap 'Post to feed' on the result screen. You can add a caption, price, and marketplace link before publishing.",
  },
  {
    q: "Can I delete a scan or account?",
    a: "Yes. Individual scans can be deleted from your closet. To delete your account, go to Settings → Account → Delete account. All your data is removed within 30 days.",
  },
  {
    q: "How do grails work?",
    a: "Grails are your wishlist. Add a search (e.g. '70s Pendleton chore coat') and Fig alerts you whenever someone posts a matching item to the feed.",
  },
  {
    q: "I found a bug or something looks wrong.",
    a: "Email support@figverified.com with a screenshot and a short description of what happened. We aim to respond within 2 business days.",
  },
];

export default function Support() {
  const nav = useNavigate();

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        {/* Top bar */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Support</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Hero */}
        <div style={{ padding: "18px 20px 0" }}>
          <div style={{
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "20px 18px",
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 900, fontSize: 28, lineHeight: 1,
              color: "hsl(var(--ink))", letterSpacing: "-0.02em",
              marginBottom: 8,
            }}>
              Support
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.5,
              marginBottom: 14,
            }}>
              Common questions, and how to reach us when something isn't right.
            </div>
            <a
              href="mailto:support@figverified.com"
              style={{
                display: "inline-block",
                background: "hsl(var(--ink))",
                color: "hsl(var(--ink-fg))",
                padding: "10px 16px",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                textDecoration: "none",
              }}
            >
              Email support →
            </a>
          </div>
        </div>

        {/* FAQ */}
        <div style={{ padding: "20px 20px 0" }}>
          <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 12 }}>
            Common questions
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {FAQS.map((item, i) => (
              <div key={i}>
                <Hairline style={{ marginBottom: 0 }} />
                <div style={{ padding: "14px 0" }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 700, fontSize: 16,
                    color: "hsl(var(--ink))", marginBottom: 6, lineHeight: 1.2,
                  }}>
                    {item.q}
                  </div>
                  <p style={{
                    margin: 0,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12, lineHeight: 1.55,
                    color: "hsl(var(--ink-2))",
                  }}>
                    {item.a}
                  </p>
                </div>
              </div>
            ))}
            <Hairline />
          </div>
        </div>

        {/* Contact card */}
        <div style={{ padding: "20px 20px 0" }}>
          <div style={{
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "16px 18px",
            textAlign: "center",
          }}>
            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
              Still need help?
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.5, marginBottom: 12,
            }}>
              Email us at support@figverified.com and we'll get back to you within 2 business days.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button
                onClick={() => nav("/faq")}
                className="btn btn-primary"
                style={{ padding: "8px 14px", fontSize: 11 }}
              >
                Full FAQ
              </button>
              <button
                onClick={() => nav("/privacy")}
                className="btn"
                style={{ padding: "8px 14px", fontSize: 11 }}
              >
                Privacy policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
